import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ArrowLeft, Mail } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { newsletterApi } from '@/lib/api';

export default function NewsletterUnsubscribePage() {
  const { id } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, already, error
  const [email, setEmail] = useState('');

  useEffect(() => {
    unsubscribe();
  }, [id]);

  const unsubscribe = async () => {
    try {
      const response = await newsletterApi.unsubscribe(id);
      if (response.data.already_unsubscribed) {
        setStatus('already');
      } else {
        setStatus('success');
        setEmail(response.data.email || '');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card p-8 md:p-12 border border-border text-center"
          >
            {status === 'loading' && (
              <>
                <div className="spinner mx-auto mb-6" />
                <p className="text-muted-foreground">Wird bearbeitet...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h1 className="font-heading text-2xl md:text-3xl mb-4">Erfolgreich abgemeldet</h1>
                <p className="text-muted-foreground mb-6">
                  {email ? (
                    <>
                      Die E-Mail-Adresse <strong>{email}</strong> wurde vom Newsletter abgemeldet.
                    </>
                  ) : (
                    <>Sie wurden erfolgreich vom Newsletter abgemeldet.</>
                  )}
                </p>
                <p className="text-sm text-muted-foreground mb-8">
                  Sie erhalten ab sofort keine Newsletter-E-Mails mehr von uns. 
                  Falls Sie sich irrtümlich abgemeldet haben, können Sie sich jederzeit 
                  wieder auf unserer Website anmelden.
                </p>
              </>
            )}

            {status === 'already' && (
              <>
                <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail size={32} className="text-amber-500" />
                </div>
                <h1 className="font-heading text-2xl md:text-3xl mb-4">Bereits abgemeldet</h1>
                <p className="text-muted-foreground mb-8">
                  Diese E-Mail-Adresse wurde bereits vom Newsletter abgemeldet.
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle size={32} className="text-red-500" />
                </div>
                <h1 className="font-heading text-2xl md:text-3xl mb-4">Fehler</h1>
                <p className="text-muted-foreground mb-8">
                  Die Abmeldung konnte nicht durchgeführt werden. 
                  Der Link ist möglicherweise ungültig oder abgelaufen.
                </p>
              </>
            )}

            <Link
              to="/"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="back-home-btn"
            >
              <ArrowLeft size={16} />
              Zur Startseite
            </Link>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
